import {
  Component,
  OnInit,
  OnDestroy,
  ChangeDetectionStrategy,
  Input
} from '@angular/core';
import {
  PopoverController,
  ModalController,
  RadioGroupCustomEvent
} from '@ionic/angular';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { LanguageService } from 'src/app/services/language.service';
import { environment } from 'src/environments/environment';

interface LanguageOption {
  code: string;
  name: string;
  nativeName: string;
  isPrimary?: boolean;
  enabled?: boolean;
  translationNoticeKey: string;
}

export type LanguageSwitcherMode = 'popover' | 'modal';

@Component({
  selector: 'pos-language-switcher',
  templateUrl: './language-switcher.component.html',
  styleUrls: ['./language-switcher.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class LanguageSwitcherComponent implements OnInit, OnDestroy {
  @Input() color = 'light';
  @Input() mode: LanguageSwitcherMode = 'popover';

  readonly showSwitcher = environment.showLanguageSwitcher;

  private readonly destroy$ = new Subject<void>();
  readonly currentLanguageCode$ = this.languageService.getCurrentLocale$;
  private primaryLanguage: string;
  selectedLanguage: string;
  triggerId = `language-trigger-${Math.random().toString(36).substr(2, 9)}`;
  isModalOpen = false;
  private availableLanguages: LanguageOption[] = [
    {
      code: 'en',
      name: 'English (EN)',
      nativeName: 'English',
      isPrimary: true,
      enabled: true,
      translationNoticeKey: 'languageSwitcher.enTranslationNotice'
    },
    {
      code: 'fr',
      name: 'Français (FR)',
      nativeName: 'French',
      isPrimary: false,
      enabled: true,
      translationNoticeKey: 'languageSwitcher.frTranslationNotice'
    }
  ];

  constructor(
    private readonly languageService: LanguageService,
    private readonly popoverController: PopoverController,
    private readonly modalController: ModalController
  ) {}

  get enabledLanguages(): LanguageOption[] {
    return this.availableLanguages.filter((lang) => lang.enabled !== false);
  }

  ngOnInit() {
    this.languageService.getCurrentLocale$
      .pipe(takeUntil(this.destroy$))
      .subscribe((currentLang) => {
        this.selectedLanguage = currentLang;
      });
  }

  onLanguageChange(languageCode: string | Event) {
    // Handle both string and event object
    this.selectedLanguage =
      typeof languageCode === 'string'
        ? languageCode
        : (languageCode as RadioGroupCustomEvent<string>).detail.value;
  }

  selectLanguage(languageCode: string) {
    this.selectedLanguage = languageCode;
  }

  async openModal() {
    if (this.mode !== 'modal') {
      return;
    }
    this.isModalOpen = true;
  }

  async closeModal() {
    this.modalController.dismiss();
    this.isModalOpen = false;
  }

  private async closePopover() {
    // Close the popover
    const popover = await this.popoverController.getTop();
    if (popover) {
      await popover.dismiss();
    }
  }

  async applyLanguageSelection() {
    if (this.selectedLanguage) {
      await this.languageService.setLanguage(this.selectedLanguage);

      // Close the appropriate interface
      if (this.mode === 'modal') {
        await this.closeModal();
      } else {
        await this.closePopover();
      }
    }
  }

  async ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
    // Ensure any open popover/modal is dismissed when component is destroyed
    await this.closePopover();
    if (this.isModalOpen) {
      await this.closeModal();
    }
  }
}
